/**
 * FilterContext — contexto global de filtros de periodo e fonte.
 *
 * Provido no nivel do AppLayout (envolve todas as rotas autenticadas com shell).
 * As rotas ROUTES_WITH_FILTERS (dashboard, mirror, radar, insights) consomem
 * este contexto para buscar dados e o topbar global le/escreve os seletores.
 *
 * Tipos exportados:
 *   PeriodMode  — '7d' | '30d' | '90d' | '365d' | 'month'
 *   SourceFilter — 'all' | 'instagram' | 'meta_ads' | 'google_drive'
 *   FilterState — snapshot readonly do estado atual
 *   FilterContextValue — o que o hook retorna
 *
 * Regras:
 *   - Zero string hardcoded (labels via i18n.ts)
 *   - Zero acesso direto a env vars
 *   - Defaults: periodo=30d, fonte=all, ano=ano atual, mes=mes atual
 *   - Modo 'month': appliedYear/appliedMonth so atualizam via onApply (botao Aplicar)
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Tipos publicos
// ---------------------------------------------------------------------------

export type PeriodMode = '7d' | '30d' | '90d' | '365d' | 'month';
export type SourceFilter = 'all' | 'instagram' | 'meta_ads' | 'google_drive';

/** Estado aplicado (o que dispara busca de dados). */
export interface FilterState {
  /** Modo do periodo: rolante ou mes especifico. */
  readonly periodMode: PeriodMode;
  /** Ano aplicado (relevante so quando periodMode === 'month'). */
  readonly appliedYear: number;
  /** Mes aplicado 1-12 (relevante so quando periodMode === 'month'). */
  readonly appliedMonth: number;
  /** Filtro de fonte. */
  readonly source: SourceFilter;
}

export interface FilterContextValue {
  /** Estado de seletor (ainda nao aplicado em modo month). */
  readonly periodMode: PeriodMode;
  readonly selectedYear: number;
  readonly selectedMonth: number;
  readonly source: SourceFilter;

  /** Estado aplicado — dispara nova busca quando muda. */
  readonly applied: FilterState;

  /** Acoes do topbar. */
  readonly onChangePeriod: (mode: PeriodMode) => void;
  readonly onChangeYear: (year: number) => void;
  readonly onChangeMonth: (month: number) => void;
  readonly onApply: () => void;
  readonly onChangeSource: (source: SourceFilter) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const FilterContext = createContext<FilterContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;

export function FilterProvider({ children }: { readonly children: React.ReactNode }): JSX.Element {
  // Seletores (estado de UI — nao aplicado ate o botao Aplicar em modo month)
  const [periodMode, setPeriodMode] = useState<PeriodMode>('30d');
  const [selectedYear, setSelectedYear] = useState<number>(CURRENT_YEAR);
  const [selectedMonth, setSelectedMonth] = useState<number>(CURRENT_MONTH);
  const [source, setSource] = useState<SourceFilter>('all');

  // Estado aplicado (dispara busca de dados)
  const [appliedYear, setAppliedYear] = useState<number>(CURRENT_YEAR);
  const [appliedMonth, setAppliedMonth] = useState<number>(CURRENT_MONTH);
  const [appliedPeriodMode, setAppliedPeriodMode] = useState<PeriodMode>('30d');
  const [appliedSource, setAppliedSource] = useState<SourceFilter>('all');

  const onChangePeriod = useCallback((mode: PeriodMode): void => {
    setPeriodMode(mode);
    // Modos rolantes aplicam imediatamente; 'month' aguarda o botao Aplicar
    if (mode !== 'month') {
      setAppliedPeriodMode(mode);
    }
  }, []);

  const onChangeYear = useCallback((year: number): void => {
    setSelectedYear(year);
    // Nao aplica ainda — aguarda onApply
  }, []);

  const onChangeMonth = useCallback((month: number): void => {
    setSelectedMonth(month);
    // Nao aplica ainda — aguarda onApply
  }, []);

  const onApply = useCallback((): void => {
    setAppliedYear(selectedYear);
    setAppliedMonth(selectedMonth);
    setAppliedPeriodMode('month');
  }, [selectedYear, selectedMonth]);

  const onChangeSource = useCallback((s: SourceFilter): void => {
    setSource(s);
    setAppliedSource(s);
  }, []);

  const applied: FilterState = useMemo(
    () => ({
      periodMode: appliedPeriodMode,
      appliedYear,
      appliedMonth,
      source: appliedSource,
    }),
    [appliedPeriodMode, appliedYear, appliedMonth, appliedSource],
  );

  const value: FilterContextValue = useMemo(
    () => ({
      periodMode,
      selectedYear,
      selectedMonth,
      source,
      applied,
      onChangePeriod,
      onChangeYear,
      onChangeMonth,
      onApply,
      onChangeSource,
    }),
    [
      periodMode,
      selectedYear,
      selectedMonth,
      source,
      applied,
      onChangePeriod,
      onChangeYear,
      onChangeMonth,
      onApply,
      onChangeSource,
    ],
  );

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useFilterContext — acessa o contexto global de filtros.
 * Lanca se chamado fora de FilterProvider.
 */
export function useFilterContext(): FilterContextValue {
  const ctx = useContext(FilterContext);
  if (ctx === null) {
    throw new Error('useFilterContext must be used within a FilterProvider');
  }
  return ctx;
}
